import { assert, Hex, NormalizedNumber } from '@sparkdotfi/common-universal'
import { useSuspenseQueries } from '@tanstack/react-query'
import { useState } from 'react'
import { useConfig } from 'wagmi'

import { ConversionRate } from '../types'
import { convertPointsRewardQueryFunction, convertPointsRewardQueryKey } from './convertPointsRewardQueryOptions'
import { POINTS_S2_CLAIM_ENABLED } from '@/config/chain/flags'
import { Token } from '@/domain/types/Token'
import { TokenSymbol } from '@/domain/types/TokenSymbol'
import { useConnectedAddress } from '@/domain/wallet/useConnectedAddress'
import { ClaimPointsRewardsObjective } from '@/features/actions/flavours/claim-points-rewards/types'
import { useActions } from '@/features/actions/logic/useActions'
import { ActionsViewState } from '@/features/actions/types'
import { PageState, PageStatus } from '@/features/dialogs/common/types'
import { usePointsTokenRepository } from '@/features/points/common/usePointsTokenRepository'
import { pointsDataQueryFunction, pointsDataQueryKey } from '@/features/points/logic/useSparkPointsData'

export interface UseConvertPointsDialogParams {
  onSuccess: (() => void) | undefined
}

export interface UseConvertPointsDialogResult {
  pageStatus: PageStatus
  spk: Token
  claimableSpk: NormalizedNumber
  pointsEarned: NormalizedNumber
  season: number
  conversionRate: ConversionRate
  actionsViewState: ActionsViewState
}

export function useConvertPointsDialog({ onSuccess }: UseConvertPointsDialogParams): UseConvertPointsDialogResult {
  const { account, chainId } = useConnectedAddress()
  const wagmiConfig = useConfig()
  const [pageStatus, setPageStatus] = useState<PageState>('form')

  const pointsTokenRepository = usePointsTokenRepository({
    chainId,
    account,
  })
  const spkToken = pointsTokenRepository.findOneBySymbol(TokenSymbol('SPK'))

  const [convertPointsRewardQueryResult, pointsDataQueryResult] = useSuspenseQueries({
    queries: [
      {
        queryKey: convertPointsRewardQueryKey({ account, chainId }),
        queryFn: () => convertPointsRewardQueryFunction({ account, wagmiConfig, chainId, spkToken }),
      },
      {
        queryKey: pointsDataQueryKey({ account }),
        queryFn: () => pointsDataQueryFunction({ account }),
      },
    ],
  })

  const rewardData = convertPointsRewardQueryResult.data
  assert(rewardData?.reward, 'No SPK reward found')

  const { reward } = rewardData

  const season = POINTS_S2_CLAIM_ENABLED
    ? reward.merkleRoot === Hex('0x39c24fe9a56d5b836300d9e6a48fc9bd57e899fe88affa65939fd9b153a6ee4d') // S1 merkle root
      ? 1
      : 2
    : 1

  const pointsEarned =
    season === 1 ? pointsDataQueryResult.data.season1TotalPoints : pointsDataQueryResult.data.season2TotalPoints
  const conversionRate: ConversionRate = {
    pointsAmount: NormalizedNumber(1_000_000),
    spkAmount: reward.cumulativeAmount.div(pointsEarned).times(1_000_000),
  }

  const objectives: ClaimPointsRewardsObjective[] = [
    {
      type: 'claimPointsRewards',
      token: spkToken,
      epoch: reward.epoch,
      cumulativeAmount: reward.cumulativeAmount,
      merkleRoot: reward.merkleRoot,
      merkleProof: reward.merkleProof,
    },
  ]

  const { viewState: actionsViewState } = useActions({
    objectives,
    context: {
      chainId,
    },
    onFinish: onSuccess ? onSuccess : () => setPageStatus('success'),
    enabled: true,
  })

  return {
    pageStatus: {
      state: pageStatus,
      actionsEnabled: true,
      goToSuccessScreen: onSuccess ? onSuccess : () => setPageStatus('success'),
    },
    spk: spkToken,
    claimableSpk: reward.cumulativeAmount,
    pointsEarned,
    season,
    conversionRate,
    actionsViewState,
  }
}
